import React, { useState, useMemo, useContext, useEffect } from "react";
import { Search, X, Check, HelpCircle } from "lucide-react";
import { MacrosContext } from "../../../utils/MacrosContext";
import VADemoMacros from "../../../libs/macros/VADemoDotPhrases.json";
import userJSON from "../../../utils/userJSON";
import sendServerLogging from "../../../utils/sendServerLogging";

export default function MacrosFeature({
  macros,
  setMacros,
  setNote,
  cursorPosition,
  setCursorPosition,
  textareaRef,
  name,
  status,
  patient,
}) {
  // Get userMacros from context
  const { userMacros, fetchUserMacros } = useContext(MacrosContext);

  useEffect(() => {
    fetchUserMacros();
  }, []);

  const suggestedDotPhrases = macros?.suggested_macros || [];

  // Initialize macros if null (for users like Dr.VADemo who don't have backend macro data)
  React.useEffect(() => {
    if (macros === null) {
      setMacros({
        suggested_macros: [],
        added_macros: [],
      });
    }
  }, [macros, setMacros]);

  const [searchTerm, setSearchTerm] = useState("");
  const [showResults, setShowResults] = useState(false);

  //state var to handle macro hover events
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const [expandedTooltip, setExpandedTooltip] = useState(null);

  // Filter dot phrases based on search term
  const filteredDotPhrases = useMemo(() => {
    if (!searchTerm.trim()) return [];

    const lowerSearchTerm = searchTerm.toLowerCase();

    // Special case for Dr.VADemo - use JSON file
    if (name === "Dr.VADemo") {
      return VADemoMacros.filter(
        (item) =>
          item.keywords?.toLowerCase().includes(lowerSearchTerm) ||
          item.dot_phrase.toLowerCase().includes(lowerSearchTerm)
      );
    }

    // For all other users, use userMacros from context
    const macrosToSearch = Array.isArray(userMacros) ? userMacros : [];

    return macrosToSearch.filter((item) => {
      // Handle both string and array keywords
      const keywordsString = Array.isArray(item.keywords)
        ? item.keywords.join(" ").toLowerCase()
        : (item.keywords || "").toLowerCase();

      return (
        keywordsString.includes(lowerSearchTerm) ||
        item.dot_phrase.toLowerCase().includes(lowerSearchTerm)
      );
    });
  }, [searchTerm, name, userMacros]);

  // Check if a dot phrase is already selected (in either accepted or added_macros)
  const isSelected = (dotPhrase) => {
    return (
      macros?.accepted?.some((item) => item.dot_phrase === dotPhrase) ||
      macros?.added_macros?.some((item) => item.dot_phrase === dotPhrase)
    );
  };

  // Check if in accepted array
  const isAccepted = (dotPhrase) => {
    return macros?.accepted?.some((item) => item.dot_phrase === dotPhrase);
  };

  // Check if in added_macros array
  const isAddedMacro = (dotPhrase) => {
    return macros?.added_macros?.some((item) => item.dot_phrase === dotPhrase);
  };

  // Handle selection from search bar - goes to added_macros
  const handleSelectDotPhrase = (selectedItem) => {
    if (!isSelected(selectedItem.dot_phrase)) {
      // Format the item to match expected structure
      const formattedItem = {
        dot_phrase: selectedItem.dot_phrase,
        value: selectedItem.content || selectedItem.value, // Use content from API or value from JSON
        keywords: Array.isArray(selectedItem.keywords)
          ? selectedItem.keywords.join(", ")
          : selectedItem.keywords,
      };

      setMacros((prev) => ({
        ...prev,
        added_macros: [...(prev.added_macros || []), formattedItem],
      }));

      // Add the macro value to the note
      setNote((prevNote) => {
        let updatedNote;
        const macroValue = selectedItem.content || selectedItem.value;

        // Check if we have a valid cursor position
        if (
          cursorPosition !== null &&
          cursorPosition !== undefined &&
          cursorPosition >= 0
        ) {
          // Insert at cursor position with a dash before the macro
          const textBefore = prevNote.substring(0, cursorPosition);
          const textAfter = prevNote.substring(cursorPosition);
          // Add a dash and space before the macro if it's not at the start of a new line
          const needsNewline =
            textBefore.length > 0 && !textBefore.endsWith("\n");
          const prefix = needsNewline ? "\n " : "";
          updatedNote = textBefore + prefix + macroValue + textAfter;

          // Update cursor position to after the inserted text (accounting for the dash and space)
          const newCursorPosition =
            cursorPosition + (prefix + macroValue).length;
          setCursorPosition(newCursorPosition);

          if (textareaRef?.current) {
            setTimeout(() => {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(
                newCursorPosition,
                newCursorPosition
              );
            }, 0);
          }
        }
        return updatedNote;
      });

      //server logging
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "MACRO_ADDED",
          eventData: { macro_added: selectedItem.dot_phrase },
          patientId: patient.patient_id,
        },
      });
    }
    setSearchTerm("");
    setShowResults(false);
  };

  // Generic remove function that checks both arrays
  const handleRemoveDotPhrase = (dotPhrase) => {
    // Check if it's in accepted array
    const acceptedItem = macros.accepted?.find(
      (item) => item.dot_phrase === dotPhrase
    );

    // Check if it's in added_macros array
    const addedItem = macros.added_macros?.find(
      (item) => item.dot_phrase === dotPhrase
    );

    const itemToRemove = acceptedItem || addedItem;

    if (acceptedItem) {
      setMacros((prev) => ({
        ...prev,
        accepted: prev.accepted?.filter(
          (item) => item.dot_phrase !== dotPhrase
        ),
      }));
    } else if (addedItem) {
      setMacros((prev) => ({
        ...prev,
        added_macros: prev.added_macros?.filter(
          (item) => item.dot_phrase !== dotPhrase
        ),
      }));
    }

    // Remove from note if item found
    if (itemToRemove) {
      setNote((prevNote) => {
        // Escape special regex characters in the value
        const escapedValue = itemToRemove.value.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&"
        );

        // Create patterns to match the macro with possible dash and newline prefixes
        const patterns = [
          // Pattern for macro with newline and dash (middle of text)
          new RegExp(`\\n-\\s*${escapedValue}`, "g"),
          // Pattern for macro with just dash (start of line)
          new RegExp(`-\\s*${escapedValue}`, "g"),
          // Original patterns for backward compatibility
          new RegExp(`(\\n\\n)?${escapedValue}\\n\\n`, "g"),
          new RegExp(escapedValue, "g"),
        ];

        let updatedNote = prevNote;

        // Try each pattern until we find a match
        for (const pattern of patterns) {
          const newNote = updatedNote.replace(pattern, "");
          if (newNote !== updatedNote) {
            updatedNote = newNote;
            break;
          }
        }

        // Clean up any extra newlines
        updatedNote = updatedNote.replace(/\n\n\n+/g, "\n\n");

        return updatedNote;
      });
    }
  };

  // Handle checkbox selection for suggested dot phrases - goes to accepted
  const handleSuggestedSelect = (item) => {
    if (isAccepted(item.dot_phrase)) {
      handleRemoveDotPhrase(item.dot_phrase);
    } else if (!isSelected(item.dot_phrase)) {
      setMacros((prev) => ({
        ...prev,
        accepted: [...(prev.accepted || []), item],
      }));

      // Add the macro value to the note at cursor position
      setNote((prevNote) => {
        let updatedNote;
        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "SUGGESTED_MACRO_ADDED",
            eventData: { mrn: patient.mrn, macro: item.dot_phrase },
            patientId: patient.patient_id,
          },
        });

        // Check if we have a valid cursor position
        if (
          cursorPosition !== null &&
          cursorPosition !== undefined &&
          cursorPosition >= 0
        ) {
          // Insert at cursor position with a dash before the macro
          const textBefore = prevNote.substring(0, cursorPosition);
          const textAfter = prevNote.substring(cursorPosition);
          // Add a dash and space before the macro if it's not at the start of a new line
          const needsNewline =
            textBefore.length > 0 && !textBefore.endsWith("\n");
          const prefix = needsNewline ? "\n- " : "- ";
          updatedNote = textBefore + prefix + item.value + textAfter;

          // Update cursor position to after the inserted text (accounting for the dash and space)
          const newCursorPosition =
            cursorPosition + (prefix + item.value).length;
          setCursorPosition(newCursorPosition);

          if (textareaRef?.current) {
            setTimeout(() => {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(
                newCursorPosition,
                newCursorPosition
              );
            }, 0);
          }
        }
        return updatedNote;
      });

       //server logging
       sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "SUGGESTED_MACRO_ADDED",
          eventData: { macro_added: item.dot_phrase },
          patientId: patient.patient_id,
        },
      });
    }
  };

  // Get all selected macros from both arrays
  const getAllSelectedMacros = () => {
    const accepted = macros?.accepted || [];
    const added = macros?.added_macros || [];
    return [...accepted, ...added];
  };

  return (
    <div className="w-full mb-4">
      <div className="space-y-6">
        {/* Selected Dot Phrases*/}
        <div>
          <h2 className="text-md italic font-semibold mb-3 text-accent1">
            Selected Dot Phrases
          </h2>
          {getAllSelectedMacros().length === 0 ? (
            <p className="text-transparent-grey text-sm">
              No dot phrases selected.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {getAllSelectedMacros().map((item, index) => (
                <div
                  key={`${item.dot_phrase}-${index}`}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-transparent-green rounded-lg border border-gray-200"
                >
                  <span className="font-mono font-semibold text-ktx-green">
                    {item.dot_phrase}
                  </span>
                  <button
                    onClick={() => handleRemoveDotPhrase(item.dot_phrase)}
                    className="p-0.5 text-gray-400 hover:text-red-600 transition-colors"
                    aria-label="Remove dot phrase"
                    disabled={status == "Locked"}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Side by Side Layout for Suggested Macros and Search */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Suggested Macros Section */}
          <div>
            <h2 className="text-md italic font-semibold mb-2 text-accent1">
              Knowtex's Suggested Macros
            </h2>

            {suggestedDotPhrases.length === 0 ? (
              <p className="text-transparent-grey text-sm">
                No suggested macros available.
              </p>
            ) : (
              <div className="space-y-3">
                {suggestedDotPhrases.map((item, index) => (
                  <div
                    key={index}
                    className="relative flex items-start p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => {
                      setHoveredIndex(null);
                      setExpandedTooltip(null);
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`suggested-${index}`}
                      checked={isAccepted(item.dot_phrase)}
                      onChange={() => handleSuggestedSelect(item)}
                      className="mt-1 mr-3 h-4 w-4 text-ktx-green rounded cursor-pointer border-gray-300 focus:ring-ktx-green"
                      disabled={status == "Locked"}
                    />
                    <label
                      htmlFor={`suggested-${index}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="font-mono font-semibold text-accent1 text-sm">
                        {item.dot_phrase}
                      </div>
                    </label>
                    {/* Tooltip */}
                    {hoveredIndex === index && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 w-64 p-3 bg-gray-800 text-white text-sm rounded shadow-lg z-10">
                        {/* Arrow pointing up */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -mb-1">
                          <div className="border-4 border-transparent border-b-gray-800"></div>
                        </div>

                        {/* Expandable content */}
                        {item.value.length > 100 ? (
                          <div>
                            <p>
                              {expandedTooltip === index
                                ? item.value
                                : `${item.value.substring(0, 100)}...`}
                            </p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedTooltip(
                                  expandedTooltip === index ? null : index
                                );
                              }}
                              className="text-blue-300 hover:text-blue-200 text-xs mt-2 underline"
                            >
                              {expandedTooltip === index
                                ? "Show less"
                                : "Show more"}
                            </button>
                          </div>
                        ) : (
                          <p>{item.value}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search Section */}
          <div>
            <h3 className="text-md italic font-semibold mb-2 text-accent1">
              Search All Dot Phrases
            </h3>

            {/* Search Input */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowResults(e.target.value.length > 0);
                }}
                onFocus={() => setShowResults(searchTerm.length > 0)}
                placeholder="Search for dot phrase..."
                className="block w-full pl-10 pr-3 py-2 italic text-light-grey border border-gray-300 rounded-lg focus:ring-2 focus:ring-ktx-green focus:border-ktx-green outline-none transition-colors"
                disabled={status == "Locked"}
                onClick={() => {
                  sendServerLogging({
                    body: {
                      ...userJSON(localStorage.getItem("userName")),
                      event: "SEARCHED_FOR_MACROS",
                      eventData: { mrn: patient.mrn },
                      patientId: patient.patient_id,
                    },
                  });
                }}
              />
            </div>

            {/* Search Results */}
            {showResults && filteredDotPhrases.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {filteredDotPhrases.map((item, index) => {
                  // Handle different property names between API and JSON
                  const displayValue = item.content || item.value;
                  const displayKeywords = Array.isArray(item.keywords)
                    ? item.keywords.join(", ")
                    : item.keywords || item.dot_phrase;

                  return (
                    <div
                      key={index}
                      onClick={() =>
                        status !== "Locked" && handleSelectDotPhrase(item)
                      }
                      className={`px-4 py-3 hover:bg-gray-50 transition-colors ${
                        isSelected(item.dot_phrase) ? "bg-gray-50" : ""
                      } ${
                        status === "Locked"
                          ? "cursor-not-allowed opacity-60"
                          : "cursor-pointer"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-ktx-green">
                              {item.dot_phrase}
                            </span>
                            {isSelected(item.dot_phrase) && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                Added
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {displayValue}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Keywords: {displayKeywords}
                          </div>
                        </div>
                        {isSelected(item.dot_phrase) && (
                          <Check className="h-5 w-5 text-green-600 flex-shrink-0 ml-2" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* No Results Message */}
            {showResults && searchTerm && filteredDotPhrases.length === 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                <p className="text-gray-500 text-center">
                  No dot phrases found for "{searchTerm}"
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
